import {useCallback, useEffect, useRef, useState} from 'react';

import type {MarketId, ProductId} from 'nutshell-core/types';

import type {ProductPickerProduct} from '../graphql/helpers/product-picker-product';
import {useGetProductsByIds} from '../graphql/hooks/use-get-products-by-ids';
import {
    cacheSelectedProductForHandoff,
    getProductCacheKey,
    getSelectedProductHandoff,
} from '../product-picker-options';

export function useProductPickerSelection({
    selectedId,
    marketId,
    onSelectedProductLoad,
}: {
    selectedId: ProductId | null | undefined;
    marketId?: MarketId;
    onSelectedProductLoad?: (product: ProductPickerProduct | null) => void;
}): {
    selectedProduct: ProductPickerProduct | null;
    isLoading: boolean;
    hasError: boolean;
    retry: () => void;
    selectProduct: (product: ProductPickerProduct) => void;
} {
    const [directSelection, setDirectSelection] = useState<{
        product: ProductPickerProduct;
        marketId?: MarketId;
        selectedIdAtSelection: ProductId | null | undefined;
    } | null>(null);
    const productCache = useRef<Map<string, ProductPickerProduct>>(new Map());
    const onLoad = useRef<typeof onSelectedProductLoad>(onSelectedProductLoad);
    const lastNotification = useRef<string | null>(null);
    onLoad.current = onSelectedProductLoad;

    const cacheKey = selectedId ? getProductCacheKey(selectedId, marketId) : null;
    const cachedProduct = cacheKey
        ? productCache.current.get(cacheKey) || getSelectedProductHandoff(selectedId!, marketId)
        : undefined;
    const {products, isLoading, error, retry} = useGetProductsByIds(
        selectedId && !cachedProduct ? [selectedId] : [],
        marketId
    );
    const hydratedProduct = selectedId
        ? products.find((product) => product.id === selectedId) || null
        : null;
    const activeDirectSelection =
        directSelection &&
        directSelection.marketId === marketId &&
        (directSelection.selectedIdAtSelection === selectedId ||
            directSelection.product.id === selectedId)
            ? directSelection
            : null;
    const selectedProduct = activeDirectSelection?.product || cachedProduct || hydratedProduct;

    useEffect(() => {
        if (!directSelection) return;
        if (
            directSelection.marketId !== marketId ||
            directSelection.selectedIdAtSelection !== selectedId
        ) {
            setDirectSelection(null);
        }
    }, [directSelection, marketId, selectedId]);

    useEffect(() => {
        if (selectedProduct && cacheKey && selectedProduct.id === selectedId) {
            productCache.current.set(cacheKey, selectedProduct);
        }

        const notificationValue = selectedProduct
            ? `product:${selectedProduct.id}`
            : error
            ? 'error'
            : !selectedId
            ? 'none'
            : isLoading
            ? null
            : 'missing';
        if (!notificationValue) return;

        const notificationKey = `${cacheKey ?? 'none'}:${notificationValue}`;
        if (lastNotification.current === notificationKey) return;

        lastNotification.current = notificationKey;
        onLoad.current?.(selectedProduct || null);
    }, [cacheKey, error, isLoading, products.length, selectedId, selectedProduct]);

    useEffect(() => {
        if (!error) return;

        const windowWithTrackJs = window as Window & {
            trackJs?: {track: (trackedError: unknown) => void};
        };
        windowWithTrackJs.trackJs?.track(error);
    }, [error]);

    const selectProduct = useCallback(
        (product: ProductPickerProduct) => {
            const selectedCacheKey = getProductCacheKey(product.id, marketId);
            productCache.current.set(selectedCacheKey, product);
            cacheSelectedProductForHandoff(product, marketId);
            setDirectSelection({product, marketId, selectedIdAtSelection: selectedId});
            lastNotification.current = `${cacheKey ?? 'none'}:product:${product.id}`;
            onLoad.current?.(product);
        },
        [cacheKey, marketId, selectedId]
    );

    return {
        selectedProduct: selectedProduct || null,
        isLoading: activeDirectSelection ? false : isLoading,
        hasError: activeDirectSelection ? false : Boolean(error),
        retry,
        selectProduct,
    };
}
